import { isSceneModel } from './models/modelFormats';
import { loadSceneModel } from './models/loadSceneModel';
import { setupRemoteFileLoader } from './remoteFileLoader';
import { createBrowserFileHandler, detectFileTypeWithContent, processFiles, convertDepthToUnified, } from './fileHandler';
import { parseLidarFile } from './parsers/lidarParser';
import { buildSparseCloud, collectColmapModelFiles, colmapReconstructionName, parseColmapModel, } from './formats/colmap/colmapFiles';
import { SPLAT_CONTAINER_REGEX } from './visualization/splatMode';
import { filmState } from './state/film.svelte';
const isVSCode = typeof acquireVsCodeApi !== 'undefined';
const SCIENTIFIC_IMAGE_ORIGINS = new Set([
    'https://images.f-kleinicke.de',
    'http://localhost:4173',
    'http://127.0.0.1:4173',
]);
const SCIENTIFIC_IMAGE_DEPTH_FILE = /\.(?:tiff?|pfm|np[yz]|png)$/i;
const receivedScientificImageHandoffs = new Set();
function trackWebsiteEvent(name) {
    const plausible = window.plausible;
    plausible?.(name);
}
export function initializeBrowserFileHandler(host) {
    host.browserFileHandler = createBrowserFileHandler((fileIndex) => host.removeFileByIndex(fileIndex), (message) => {
        // Route messages to the appropriate handlers
        switch (message.type) {
            case 'cameraParamsResult':
                host.handleCameraParams(message);
                break;
            case 'cameraParamsWithScaleResult':
                host.handleCameraParams(message);
                break;
            default:
                console.log(`🌐 Unhandled browser message: ${message.type}`);
                break;
        }
    });
}
export function handleBrowserMessage(host, message) {
    if (!host.browserFileHandler) {
        console.error('🌐 Browser file handler not initialized');
        return;
    }
    switch (message.type) {
        case 'removeFile':
            host.browserFileHandler.removeFile(message.fileIndex);
            break;
        case 'requestCameraParams':
            host.browserFileHandler.handleCameraParams(message);
            break;
        case 'requestCameraParamsWithScale':
            host.browserFileHandler.handleCameraParamsWithScale(message);
            break;
        case 'savePlyFile':
            host.browserFileHandler.savePlyFile(message);
            break;
        default:
            console.log(`🌐 Browser mode: Unhandled message type ${message.type}`);
            break;
    }
}
export function setupPanelResizeAndDrag() {
    const mainPanel = document.getElementById('main-ui-panel');
    const tabContent = document.querySelector('.tab-content');
    if (!mainPanel || !tabContent) {
        console.warn('⚠️ Main panel or tab content not found');
        return;
    }
    console.log('✅ Panel resize setup initialized');
    // Panel resize functionality - drag from bottom edge
    let isDragging = false;
    let startY = 0;
    let startHeight = 0;
    const resizeZone = 10; // 10px from bottom edge for easier grabbing
    // Helper to check if mouse is in resize zone
    const isInResizeZone = (e) => {
        const rect = mainPanel.getBoundingClientRect();
        const mouseY = e.clientY;
        const bottomEdge = rect.bottom;
        return mouseY >= bottomEdge - resizeZone && mouseY <= bottomEdge + 2;
    };
    // Update cursor when hovering over resize zone
    mainPanel.addEventListener('mousemove', (e) => {
        if (!isDragging) {
            if (isInResizeZone(e)) {
                mainPanel.style.cursor = 'ns-resize';
            }
            else {
                mainPanel.style.cursor = '';
            }
        }
    });
    mainPanel.addEventListener('mouseleave', () => {
        if (!isDragging) {
            mainPanel.style.cursor = '';
        }
    });
    // Start dragging
    mainPanel.addEventListener('mousedown', (e) => {
        if (isInResizeZone(e)) {
            isDragging = true;
            startY = e.clientY;
            // Get current ACTUAL height (not max-height from CSS)
            const currentMaxHeight = tabContent.style.maxHeight;
            if (currentMaxHeight && currentMaxHeight !== '') {
                // Already has an inline style - use it
                if (currentMaxHeight.includes('vh')) {
                    const vh = parseFloat(currentMaxHeight);
                    startHeight = (vh / 100) * window.innerHeight;
                }
                else {
                    startHeight = parseInt(currentMaxHeight);
                }
            }
            else {
                // No inline style yet - get the actual computed height
                const computedHeight = tabContent.getBoundingClientRect().height;
                startHeight = computedHeight;
                console.log('📏 Using computed height:', computedHeight);
            }
            mainPanel.style.cursor = 'ns-resize';
            document.body.style.cursor = 'ns-resize';
            e.preventDefault();
            e.stopPropagation();
            console.log('🖱️ Drag started, initial height:', startHeight);
        }
    });
    // Handle dragging
    document.addEventListener('mousemove', (e) => {
        if (isDragging) {
            const deltaY = e.clientY - startY;
            const newHeight = startHeight + deltaY;
            // Clamp height between reasonable values (in pixels)
            const minHeight = 150; // Minimum 150px
            const maxHeight = window.innerHeight * 0.9; // Maximum 90vh
            const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
            tabContent.style.maxHeight = `${clampedHeight}px`;
            e.preventDefault();
        }
    });
    // Stop dragging
    document.addEventListener('mouseup', () => {
        if (isDragging) {
            isDragging = false;
            mainPanel.style.cursor = '';
            document.body.style.cursor = '';
            console.log('🖱️ Drag ended');
        }
    });
}
export function setupBrowserFileHandlers(host) {
    setupRemoteFileLoader(host, isVSCode);
    const fileInput = document.getElementById('hiddenFileInput');
    const addFileButton = document.getElementById('add-file');
    const mainPanel = document.getElementById('main-ui-panel');
    if (fileInput) {
        fileInput.addEventListener('change', event => {
            const files = event.target.files;
            if (files) {
                handleDroppedFiles(host, Array.from(files));
            }
        });
    }
    // Add drag & drop support to the Add Point Cloud button
    if (addFileButton) {
        addFileButton.addEventListener('dragover', event => {
            handleDragOver(event);
            addFileButton.style.backgroundColor = '#1177bb';
            addFileButton.style.transform = 'scale(1.02)';
        });
        addFileButton.addEventListener('dragleave', () => {
            addFileButton.style.backgroundColor = '';
            addFileButton.style.transform = '';
        });
        addFileButton.addEventListener('drop', event => {
            addFileButton.style.backgroundColor = '';
            addFileButton.style.transform = '';
            void handleDropEvent(host, event);
        });
    }
    // Also add drag & drop to the entire main UI panel as fallback
    if (mainPanel) {
        mainPanel.addEventListener('dragover', event => {
            handleDragOver(event);
        });
        mainPanel.addEventListener('drop', event => {
            void handleDropEvent(host, event);
        });
    }
    // Add drag & drop support to the entire window
    document.addEventListener('dragover', event => {
        handleDragOver(event);
        // Add visual feedback to the entire window
        document.body.style.backgroundColor = 'rgba(0, 95, 184, 0.1)';
    });
    document.addEventListener('dragleave', event => {
        // Only remove highlight when leaving the entire document
        if (!event.relatedTarget || event.relatedTarget === document.documentElement) {
            document.body.style.backgroundColor = '';
        }
    });
    document.addEventListener('drop', event => {
        document.body.style.backgroundColor = '';
        void handleDropEvent(host, event);
    });
    if (!isVSCode) {
        setupScientificImageHandoff(host);
    }
}
/** Receive a local depth image directly from images.f-kleinicke.de. */
export function setupScientificImageHandoff(host) {
    window.addEventListener('message', event => {
        if (!SCIENTIFIC_IMAGE_ORIGINS.has(event.origin)) {
            return;
        }
        const message = event.data;
        const id = typeof message?.id === 'string' ? message.id : '';
        if (!id) {
            return;
        }
        if (message.type === 'scientific-image-handoff-probe') {
            event.source?.postMessage({ type: 'scientific-image-viewer-ready', id }, event.origin);
            return;
        }
        if (message.type !== 'scientific-image-depth' ||
            receivedScientificImageHandoffs.has(id) ||
            !(message.data instanceof ArrayBuffer)) {
            return;
        }
        const fileName = String(message.fileName || '')
            .split(/[\\/]/)
            .pop() || 'depth.tiff';
        if (!SCIENTIFIC_IMAGE_DEPTH_FILE.test(fileName)) {
            host.showError(`The Scientific Image Visualizer sent an unsupported depth format: ${fileName}`);
            return;
        }
        receivedScientificImageHandoffs.add(id);
        void handleBrowserFiles(host, [
            new File([message.data], fileName, {
                type: 'application/octet-stream',
            }),
        ]);
    });
}
export function handleDragOver(event) {
    event.preventDefault();
    if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
    }
}
export async function handleDropEvent(host, event) {
    event.preventDefault();
    event.stopPropagation();
    document.body.style.backgroundColor = '';
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length > 0) {
        await handleDroppedFiles(host, files);
        return;
    }
    if (isVSCode) {
        const filePaths = extractDroppedFilePaths(event.dataTransfer);
        if (filePaths.length > 0) {
            host.showImmediateLoading({ fileName: `${filePaths.length} dropped file(s)` });
            filePaths.forEach((filePath, index) => {
                // Each path finishes independently in the extension host, so each one
                // needs a matching slot in the webview's pending-load counter.
                if (index > 0) {
                    host.showImmediateLoading({ fileName: `${filePaths.length} dropped file(s)` });
                }
                host.vscode.postMessage({
                    type: 'addFileFromPath',
                    path: filePath,
                });
            });
            return;
        }
    }
}
export function extractDroppedFilePaths(dataTransfer) {
    if (!dataTransfer) {
        return [];
    }
    const rawValues = [
        dataTransfer.getData('text/uri-list'),
        dataTransfer.getData('text/plain'),
    ].filter(Boolean);
    const paths = [];
    for (const rawValue of rawValues) {
        for (const rawLine of rawValue.split(/\r?\n/)) {
            const line = rawLine.trim();
            if (!line || line.startsWith('#')) {
                continue;
            }
            if (line.startsWith('file://')) {
                try {
                    const url = new URL(line);
                    let pathName = decodeURIComponent(url.pathname);
                    if (/^\/[A-Za-z]:\//.test(pathName)) {
                        pathName = pathName.slice(1);
                    }
                    paths.push(pathName);
                }
                catch {
                    // Ignore malformed drag payloads and keep checking other entries.
                }
            }
            else if (/^(\/|[A-Za-z]:\\)/.test(line)) {
                paths.push(line);
            }
        }
    }
    return Array.from(new Set(paths));
}
export async function handleDroppedFiles(host, files) {
    if (files.length === 0) {
        return;
    }
    if (isVSCode) {
        host.showImmediateLoading({ fileName: `${files.length} dropped file(s)` });
        try {
            const droppedFiles = await Promise.all(files.map(async (file) => ({
                name: file.name,
                data: await file.arrayBuffer(),
            })));
            host.vscode.postMessage({
                type: 'addDroppedFiles',
                files: droppedFiles,
            });
        }
        catch (error) {
            host.showError(`Failed to read dropped file: ${error instanceof Error ? error.message : String(error)}`);
        }
        return;
    }
    await handleBrowserFiles(host, files);
}
export async function loadExamplePointCloud(host, kind = 'guided') {
    if (isVSCode) {
        return;
    }
    if (kind === 'basic') {
        try {
            const response = await fetch('examples/example-point-cloud.ply');
            if (!response.ok) {
                throw new Error(`download returned ${response.status}`);
            }
            const file = new File([await response.arrayBuffer()], 'example-point-cloud.ply', {
                type: 'application/octet-stream',
            });
            await handleBrowserFiles(host, [file]);
            trackWebsiteEvent('Example Point Cloud Loaded');
        }
        catch (error) {
            host.showError(`Failed to load the basic example point cloud: ${error instanceof Error ? error.message : String(error)}`);
        }
        return;
    }
    try {
        const [pointCloudResponse, cameraPathResponse, measurementPathResponse] = await Promise.all([
            fetch('/examples/test_pc2_binary-v1.7.0.ply'),
            fetch('examples/camera-path-2026-08-27-14-27-47.json'),
            fetch('examples/measurement-paths-2026-08-27-15-49-45.json'),
        ]);
        if (!pointCloudResponse.ok) {
            throw new Error(`point-cloud download returned ${pointCloudResponse.status}`);
        }
        if (!cameraPathResponse.ok) {
            throw new Error(`camera-path download returned ${cameraPathResponse.status}`);
        }
        if (!measurementPathResponse.ok) {
            throw new Error(`measurement-path download returned ${measurementPathResponse.status}`);
        }
        const firstExampleIndex = host.spatialFiles.length;
        const file = new File([await pointCloudResponse.arrayBuffer()], 'test_pc2_binary.ply', {
            type: 'application/octet-stream',
        });
        await handleBrowserFiles(host, [file]);
        if (host.spatialFiles.length <= firstExampleIndex) {
            throw new Error('the point cloud could not be displayed');
        }
        // This example intentionally starts as a clean white cloud, while the
        // per-file colour picker still offers its embedded RGB data as Original.
        host.onFileColorModeChange(firstExampleIndex, '0');
        host.setOpenGLCameraConvention();
        const measurementPath = await measurementPathResponse.text();
        if (!host.loadMeasurementPathProject(measurementPath)) {
            throw new Error('the bundled measurement path is invalid');
        }
        const cameraPath = await cameraPathResponse.text();
        if (!host.filmManager?.loadProject(cameraPath)) {
            throw new Error('the bundled camera path is invalid');
        }
        filmState.loop = true;
        host.filmManager.play();
        trackWebsiteEvent('Example Point Cloud Loaded');
    }
    catch (error) {
        host.showError(`Failed to load the example point cloud: ${error instanceof Error ? error.message : String(error)}`);
    }
}
export async function handleBrowserFiles(host, files) {
    console.log(`🌐 Loading ${files.length} files in browser...`);
    host.showImmediateLoading({ fileName: `${files.length} files`, pointCount: 0 });
    try {
        const modelFiles = files.filter(file => isSceneModel(file.name));
        for (const file of modelFiles) {
            const model = await loadSceneModel({
                bytes: new Uint8Array(await file.arrayBuffer()),
                fileName: file.name,
                files,
            });
            await host.displayFiles([model]);
        }
        if (modelFiles.length) {
            files = files.filter(file => !isSceneModel(file.name) && !/\.(bin|png|jpe?g|tga|bmp|webp|gif|mtl)$/i.test(file.name));
            if (!files.length) {
                return;
            }
        }
        // Convert File objects to data format expected by shared function
        const fileData = await Promise.all(files.map(async (file) => ({
            name: file.name,
            data: new Uint8Array(await file.arrayBuffer()),
        })));
        // Separate depth files and JSON files for special handling
        const depthFiles = [];
        const regularFiles = [];
        for (const file of fileData) {
            const fileType = await detectFileTypeWithContent(file.name, file.data);
            if (fileType?.isDepthFile) {
                depthFiles.push(file);
            }
            else if (fileType?.category === 'poseData') {
                // JSON files are handled separately below, don't add to regularFiles
                // to avoid double processing
            }
            else {
                regularFiles.push(file);
            }
        }
        const spatialDataArray = [];
        // Remember starting index to map newly added files
        const baseIndexStart = host.spatialFiles.length;
        // Track depth metadata to populate fileDepthData after display
        const depthMetaRecords = [];
        // A COLMAP sparse model arrives as several files at once (cameras/images/
        // points3D). They are recognised as a set and consumed together, so they
        // never reach the per-file pipelines below.
        const colmap = collectColmapModelFiles(regularFiles);
        if (colmap) {
            try {
                const model = parseColmapModel(colmap.model);
                const cloud = buildSparseCloud(model, colmapReconstructionName(regularFiles));
                if (cloud) {
                    cloud.metadata = { ...cloud.metadata, colmapModel: model };
                    spatialDataArray.push(cloud);
                }
                else {
                    host.showError('COLMAP model has no points3D file; load it alongside cameras and images to see the sparse cloud.');
                }
            }
            catch (error) {
                host.showError(`Failed to read COLMAP model: ${error instanceof Error ? error.message : String(error)}`);
            }
        }
        const remainingFiles = colmap
            ? regularFiles.filter(file => !colmap.consumed.has(file.name))
            : regularFiles;
        const lidarFiles = remainingFiles.filter(file => /\.(las|laz|e57)$/i.test(file.name));
        const splatContainerFiles = remainingFiles.filter(file => SPLAT_CONTAINER_REGEX.test(file.name));
        const conventionalFiles = remainingFiles.filter(file => !/\.(las|laz|e57)$/i.test(file.name) && !SPLAT_CONTAINER_REGEX.test(file.name));
        for (const file of lidarFiles) {
            try {
                const extension = file.name.split('.').pop().toLowerCase();
                spatialDataArray.push(...(await parseLidarFile(file.data, extension, file.name)));
            }
            catch (error) {
                host.showError(`Failed to load ${file.name}: ${error instanceof Error ? error.message : String(error)}`);
            }
        }
        // Splat-native containers (.spz/.splat/.ksplat/.sog): decoded by Spark,
        // centers join the scene as a point cloud, splat mode turns on after add.
        for (const file of splatContainerFiles) {
            try {
                spatialDataArray.push(await host.splatMode.loadContainer(file.name, file.data));
            }
            catch (error) {
                host.showError(`Failed to load ${file.name}: ${error instanceof Error ? error.message : String(error)}`);
            }
        }
        // Process conventional files using shared functionality
        if (conventionalFiles.length > 0) {
            const parseResults = await processFiles(conventionalFiles, {
                timingCallback: (message) => {
                    console.log(`⏱️ ${message}`);
                },
                progressCallback: (current, total, fileName) => {
                    console.log(`📁 Processing ${fileName} (${current}/${total})`);
                },
                errorCallback: (error) => {
                    console.error(`❌ Error processing ${error.fileName}:`, error.error);
                    host.showError(error.error);
                },
            });
            // Convert parse results to SpatialData format
            parseResults.forEach(result => {
                spatialDataArray.push(result.data);
            });
        }
        // Handle depth files using the unified flow
        for (const depthFile of depthFiles) {
            console.log(`🖼️ Depth image detected: ${depthFile.name}`);
            try {
                // Ask for params (prompt); then convert via shared helper
                const params = await host.promptForCameraParameters(depthFile.name);
                if (!params) {
                    console.log(`⏭️ Skipping ${depthFile.name} - camera parameters cancelled`);
                    continue;
                }
                const parse = await convertDepthToUnified(depthFile.name, depthFile.data.buffer, {
                    fx: params.fx,
                    fy: params.fy ?? params.fx,
                    cx: params.cx ?? undefined,
                    cy: params.cy ?? undefined,
                    cameraModel: params.cameraModel,
                    depthType: params.depthType,
                    convention: params.convention ?? 'opengl',
                    baseline: params.baseline,
                    pngScaleFactor: params.pngScaleFactor,
                    depthScale: params.depthScale,
                    depthBias: params.depthBias,
                });
                const data = parse.data;
                data.isDepthDerived = true;
                // Record dimensions if provided
                const dims = parse.data.depthDimensions;
                if (dims) {
                    data.depthDimensions = dims;
                }
                const localIndex = spatialDataArray.length;
                spatialDataArray.push(data);
                depthMetaRecords.push({
                    localIndex,
                    fileName: depthFile.name,
                    buffer: depthFile.data.buffer,
                    params,
                    dims,
                });
            }
            catch (error) {
                console.error(`❌ Error processing depth image ${depthFile.name}:`, error);
                host.showError(`Failed to process depth image ${depthFile.name}: ${error}`);
            }
        }
        // Handle JSON files - check if they're camera profiles or pose data
        const jsonFiles = [];
        for (const file of fileData) {
            const fileType = await detectFileTypeWithContent(file.name, file.data);
            if (fileType?.category === 'poseData') {
                jsonFiles.push(file);
            }
        }
        for (const file of jsonFiles) {
            console.log(`📍 JSON file detected: ${file.name}`);
            try {
                // Parse JSON to determine if it's a camera profile or pose data
                const jsonText = new TextDecoder().decode(file.data);
                const jsonData = JSON.parse(jsonText);
                // Check if this is a camera profile JSON
                if (jsonData && jsonData.cameras && typeof jsonData.cameras === 'object') {
                    console.log(`📷 Camera profile detected: ${file.name}`);
                    host.handleCameraProfile(jsonData, file.name);
                }
                else {
                    console.log(`📍 Pose data detected: ${file.name}`);
                    // Handle pose data using the existing method
                    await host.handlePoseData({ data: jsonData, fileName: file.name });
                }
            }
            catch (error) {
                console.error(`❌ Error parsing JSON file ${file.name}:`, error);
                host.showError(`Failed to parse JSON file ${file.name}: ${error}`);
            }
        }
        if (spatialDataArray.length > 0) {
            await host.displayFiles(spatialDataArray);
            // Populate fileDepthData for newly added depth-derived files.
            // The key is the index addNewFiles() actually assigned, read back off the
            // object - never `baseIndexStart + localIndex`. That sum was computed
            // before the camera-parameter prompt, and anything loaded while the
            // prompt was open (another drop, an example cloud) pushes this batch
            // further down, so the guess would file the depth data under a different
            // file and leave the real one with no cache to reproject from.
            for (const rec of depthMetaRecords) {
                const fileIndex = spatialDataArray[rec.localIndex]?.fileIndex ?? baseIndexStart + rec.localIndex;
                host.fileDepthData.set(fileIndex, {
                    originalData: rec.buffer,
                    fileName: rec.fileName,
                    cameraParams: rec.params,
                    depthDimensions: rec.dims || { width: 0, height: 0 },
                });
                if (rec.dims) {
                    // Ensure cx/cy fields are populated correctly in UI
                    host.updatePrinciplePointFields(fileIndex, rec.dims);
                }
            }
        }
    }
    catch (error) {
        console.error('Error loading files:', error);
        host.showError(`Failed to load files: ${error instanceof Error ? error.message : String(error)}`);
    }
}
